import { Key, useCallback, useMemo } from "react";

import { DataNode } from "antd/es/tree";
import { IntlShape } from "react-intl";

import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";

import { getFieldCategoryDisplayName } from "@app/util/ui";

import { FIELD_PREFIX } from "./const";

const useFieldTree = (
  intl: IntlShape,
  categories: FieldCategory[],
  categorizedFields: Partial<Record<FieldCategory, IField[]>>,
  selectedFields: IField["name"][], // all selected fields, not just from this category
  query: string,
  checkInFieldsVisibility: boolean,
  fixedFields?: IField[],
  showCategories?: boolean
): [
  data: DataNode[],
  checkedKeys: {
    checked: Key[];
    halfChecked: Key[];
  }
] => {
  const fixedFieldsNames = useMemo(() => {
    return new Set(fixedFields?.map((field) => field.name) ?? []);
  }, [fixedFields]);

  const getItemElement = useCallback(
    (field: IField) => ({
      disabled: field.hasFeature(FieldFeature.ALWAYS_VISIBLE) || fixedFieldsNames.has(field.name),
      key: `${FIELD_PREFIX}${field.name}`,
      title: field.displayName,
    }),
    [fixedFieldsNames]
  );

  const preparedCategorizedFields: Partial<Record<FieldCategory, IField[]>> = useMemo(() => {
    const result: Partial<Record<FieldCategory, IField[]>> = {};
    Object.keys(categorizedFields).forEach((category) => {
      const fields = (categorizedFields[category as FieldCategory] as IField[])
        .filter((field) => !field.hasFeature(FieldFeature.CHECK_IN) || checkInFieldsVisibility)
        .sort((a: IField, b: IField) => a.displayName.localeCompare(b.displayName));
      if (fields.length > 0) {
        result[category as FieldCategory] = fields;
      }
    });
    return result;
  }, [categorizedFields, checkInFieldsVisibility]);

  const filteredCategorizedFields: Partial<Record<FieldCategory, IField[]>> = useMemo(() => {
    if (!query) {
      return preparedCategorizedFields;
    }
    const result: Partial<Record<FieldCategory, IField[]>> = {};
    Object.keys(preparedCategorizedFields).forEach((category) => {
      const fields = (preparedCategorizedFields[category as FieldCategory] as IField[]).filter(
        ({ displayName }) => displayName.toLowerCase().includes(query)
      );
      if (fields.length > 0) {
        result[category as FieldCategory] = fields;
      }
    });

    return result;
  }, [preparedCategorizedFields, query]);

  const [checkedCategoryKeys, halfCheckedCategoryKeys]: [Key[], Key[]] = useMemo(() => {
    const checkedCategoryKeys: Key[] = [],
      halfCheckedCategoryKeys: Key[] = [];
    Object.keys(preparedCategorizedFields).forEach((key) => {
      const countOfSelectedFields =
        preparedCategorizedFields[key as FieldCategory]?.filter(({ name }) =>
          selectedFields.includes(name)
        ).length ?? 0;
      if (preparedCategorizedFields[key as FieldCategory]?.length === countOfSelectedFields) {
        checkedCategoryKeys.push(key);
      } else if (countOfSelectedFields > 0) {
        halfCheckedCategoryKeys.push(key);
      }
    });
    return [checkedCategoryKeys, halfCheckedCategoryKeys];
  }, [preparedCategorizedFields, selectedFields]);

  const data = useMemo(() => {
    return showCategories
      ? categories
          .filter((category) => filteredCategorizedFields[category]?.length)
          .map((category) => ({
            children: filteredCategorizedFields[category]!.map(getItemElement),
            key: category,
            title: getFieldCategoryDisplayName(intl, category),
          }))
      : Object.values(filteredCategorizedFields).flat().map(getItemElement);
  }, [categories, filteredCategorizedFields, getItemElement, intl, showCategories]);

  return [
    data,
    {
      checked: [...checkedCategoryKeys, ...selectedFields.map((name) => `${FIELD_PREFIX}${name}`)],
      halfChecked: halfCheckedCategoryKeys,
    },
  ];
};

export default useFieldTree;
